import {
  safeLocalStorageGet,
  safeLocalStorageSet,
  safeSessionStorageGet,
  safeSessionStorageRemove,
  safeSessionStorageSet,
} from "@/lib/safe-browser";

declare const __ROUTIFY_BUILD_VERSION__: string;

const LOG_KEY = "routify:pwa:logs";
const LAST_RECOVERY_KEY = "routify:pwa:last-recovery-at";
const LAST_RELOAD_KEY = "routify:pwa:last-reload-reason";
const MIN_RECOVERY_GAP_MS = 8_000;
const APP_SCOPE = "/";

let initialized = false;
let reloading = false;

function getBuildVersion() {
  try {
    return __ROUTIFY_BUILD_VERSION__ || "unknown";
  } catch {
    return "unknown";
  }
}

function isAndroid() {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  const iosStandalone = "standalone" in window.navigator && Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return Boolean(standalone || iosStandalone);
}

function appScopeUrl() {
  return `${window.location.origin}${APP_SCOPE}`;
}

function isAppServiceWorker(registration: ServiceWorkerRegistration) {
  return registration.scope === appScopeUrl();
}

function isAppCacheName(name: string) {
  const scope = appScopeUrl();
  return (
    name.startsWith("routify-") ||
    (name.endsWith(scope) && /(^|-)precache-v\d+-|(^|-)runtime-|(^|-)googleAnalytics-/.test(name))
  );
}

function readLogs(): unknown[] {
  const raw = safeLocalStorageGet(LOG_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function logPwa(event: string, details: Record<string, unknown> = {}) {
  const entry = {
    at: new Date().toISOString(),
    event,
    build: getBuildVersion(),
    android: isAndroid(),
    pwa: isStandalonePwa(),
    path: typeof window !== "undefined" ? window.location.pathname : "",
    ...details,
  };

  try {
    console.info("[Routify PWA]", entry);
  } catch {
    // Console can be unavailable in older WebViews.
  }

  const logs = [...readLogs(), entry].slice(-40);
  safeLocalStorageSet(LOG_KEY, JSON.stringify(logs));
}

async function clearAppCaches() {
  if (typeof window === "undefined" || !("caches" in window)) return;
  const cacheNames = await caches.keys();
  const appCacheNames = cacheNames.filter(isAppCacheName);
  await Promise.allSettled(appCacheNames.map((name) => caches.delete(name)));
  logPwa("caches-cleared", { count: appCacheNames.length, names: appCacheNames });
}

async function unregisterAppServiceWorkers() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  const appRegistrations = registrations.filter(isAppServiceWorker);
  await Promise.allSettled(appRegistrations.map((registration) => registration.unregister()));
  logPwa("service-workers-unregistered", { count: appRegistrations.length });
}

function canRecoverNow(reason: string) {
  const now = Date.now();
  const last = Number(safeSessionStorageGet(LAST_RECOVERY_KEY) || "0");
  if (Number.isFinite(last) && now - last < MIN_RECOVERY_GAP_MS) {
    logPwa("recovery-suppressed", { reason, msSinceLast: now - last });
    return false;
  }
  safeSessionStorageSet(LAST_RECOVERY_KEY, String(now));
  return true;
}

async function controlledReload(
  reason: string,
  options: { clearCaches?: boolean; unregisterServiceWorker?: boolean; cacheBustUrl?: boolean } = {},
) {
  if (reloading || !canRecoverNow(reason)) return;
  reloading = true;

  const { clearCaches = false, unregisterServiceWorker = false, cacheBustUrl = clearCaches || unregisterServiceWorker } = options;
  logPwa("controlled-reload-start", { reason, clearCaches, unregisterServiceWorker, cacheBustUrl });
  safeSessionStorageSet(LAST_RELOAD_KEY, reason);

  try {
    if (clearCaches) await clearAppCaches();
    if (unregisterServiceWorker) await unregisterAppServiceWorkers();
  } catch (error) {
    logPwa("controlled-reload-cleanup-error", { reason, message: String(error) });
  } finally {
    window.setTimeout(() => {
      if (!cacheBustUrl) {
        window.location.reload();
        return;
      }
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set("routify_refresh", String(Date.now()));
      window.location.replace(nextUrl.toString());
    }, 150);
  }
}

function getErrorText(error: unknown) {
  if (typeof error === "string") return error;
  if (error instanceof Error) return `${error.name}: ${error.message}\n${error.stack ?? ""}`;
  if (error && typeof error === "object") {
    const fields = error as Record<string, unknown>;
    return [fields.message, fields.reason, fields.filename, fields.srcElement, fields.target]
      .map((value) => String(value ?? ""))
      .join("\n");
  }
  return String(error ?? "");
}

export function isStaleAssetError(error: unknown) {
  const text = getErrorText(error);
  const staleMessage =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to load module script|Unable to preload CSS|Loading chunk|ChunkLoadError|Loading CSS chunk|dynamically imported module|__vitePreload/i.test(
      text,
    );
  const assetReference = /\/assets\/|\.js(\?|$)|\.css(\?|$)|module script/i.test(text);
  return staleMessage || (assetReference && /failed|error|load|fetch|import/i.test(text));
}

export function requestPwaRecovery(error: unknown, reason = "chunk-or-import-error") {
  if (typeof window === "undefined") return false;
  if (!isStaleAssetError(error)) return false;
  logPwa("stale-asset-error", { reason, message: getErrorText(error).slice(0, 500) });
  void controlledReload(reason, { clearCaches: true, unregisterServiceWorker: true });
  return true;
}

function setupGlobalErrorFallback() {
  window.addEventListener("error", (event) => {
    const target = event.target as HTMLElement | null;
    let targetUrl = "";
    if (target instanceof HTMLScriptElement) targetUrl = target.src;
    if (target instanceof HTMLLinkElement) targetUrl = target.href;
    requestPwaRecovery(`${event.message || "asset error"}\n${event.filename || ""}\n${targetUrl}`, "window-error");
  });

  window.addEventListener("unhandledrejection", (event) => {
    requestPwaRecovery(event.reason, "unhandled-rejection");
  });
}

export function setupPwaRecovery() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  const lastReloadReason = safeSessionStorageGet(LAST_RELOAD_KEY);
  if (lastReloadReason) {
    logPwa("loaded-after-controlled-reload", { reason: lastReloadReason });
    safeSessionStorageRemove(LAST_RELOAD_KEY);
  }
  const refreshParam = new URLSearchParams(window.location.search).get("routify_refresh");
  if (refreshParam) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("routify_refresh");
    window.history.replaceState(window.history.state, "", cleanUrl.toString());
  }

  setupGlobalErrorFallback();
  logPwa("init", {
    swSupported: "serviceWorker" in navigator,
    standalone: isStandalonePwa(),
  });

  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    unregisterAppServiceWorkers()
      .then(() => clearAppCaches())
      .catch((error) => logPwa("sw-cleanup-error", { message: String(error) }));
  }
}
